import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Heart, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CONTESTANT } from "@/constants/testIds";

export default function Contestants() {
  const [list, setList] = useState([]);
  const [myVotes, setMyVotes] = useState(new Set());
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const q = params.get("q") || "";
  const stateFilter = params.get("state") || "";

  useEffect(() => {
    api.get("/contestants").then((r) => setList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) {
      setMyVotes(new Set());
      return;
    }
    api
      .get("/me/votes")
      .then((r) => setMyVotes(new Set(r.data.contestant_ids)))
      .catch(() => {});
  }, [user]);

  const filtered = useMemo(() => {
    return list.filter((c) => {
      if (stateFilter && c.state.toLowerCase() !== stateFilter.toLowerCase()) return false;
      if (q) {
        const t = q.toLowerCase();
        return (
          c.name.toLowerCase().includes(t) ||
          c.state.toLowerCase().includes(t) ||
          (c.farm_focus || "").toLowerCase().includes(t)
        );
      }
      return true;
    });
  }, [list, q, stateFilter]);

  const vote = async (id) => {
    if (!user) {
      toast.error("Please register or login to vote.");
      return;
    }
    if (myVotes.has(id)) {
      toast.info("You've already voted for this contestant.");
      return;
    }
    try {
      const r = await api.post(`/contestants/${id}/vote`);
      setMyVotes(new Set([...myVotes, id]));
      setList((prev) =>
        prev.map((c) => (c.id === id ? { ...c, votes: r.data.votes } : c)),
      );
      toast.success("Vote recorded!");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
      <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] mb-5">
        37 Contestants · One Champion
      </div>
      <h1
        className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-8"
        style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
      >
        Meet the<br />
        <span className="text-[#F26A1F]">champions</span>.
      </h1>

      <div className="flex items-center gap-3 mb-12 max-w-md">
        <div className="flex items-center gap-3 h-12 px-4 rounded-sm border border-white/10 bg-white/[0.03] flex-1 focus-within:border-[#F26A1F] transition-colors">
          <SearchIcon size={16} className="text-white/40" />
          <input
            value={q}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set("q", e.target.value);
              else next.delete("q");
              setParams(next);
            }}
            placeholder="Search by name, state, or focus..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
            data-testid="contestants-search-input"
          />
        </div>
        {(q || stateFilter) && (
          <button
            onClick={() => setParams({})}
            className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#F26A1F]"
          >
            Clear
          </button>
        )}
      </div>

      {!user && (
        <div className="mb-10 p-5 border border-[#F26A1F]/40 bg-[#F26A1F]/5 text-sm flex flex-wrap items-center justify-between gap-4">
          <span className="text-white/80">
            <Link to="/register" className="text-[#F26A1F] font-bold underline">Register</Link> or
            {" "}
            <Link to="/login" className="text-[#F26A1F] font-bold underline">login</Link> to vote
            for your favorite contestant.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map((c) => {
          const voted = myVotes.has(c.id);
          return (
            <div
              key={c.id}
              data-testid={CONTESTANT.card}
              className="group border border-white/10 hover:border-[#F26A1F]/60 transition-colors bg-black"
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-black flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_farm-show-hub/artifacts/e3nn2b3x_WhatsApp%20Image%202026-05-30%20at%2012.21.52%20AM.jpeg"
                  alt="Agrovest contestant placeholder"
                  className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-85 group-hover:scale-105 transition-all duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/30" />
                <div className="absolute top-3 left-3 px-2 py-1 bg-black/80 text-[10px] uppercase tracking-[0.2em] text-[#F26A1F] font-bold">
                  {c.state}
                </div>
                <div className="absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.2em] text-[#2BA84A] font-bold">
                  Awaiting
                </div>
              </div>
              <div className="p-4">
                <div className="font-bold tracking-tight">{c.state} Representative</div>
                <div className="text-xs text-white/50 mt-1">Contestant pending audition</div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs text-white/60">
                    <span className="text-[#2BA84A] font-bold">{c.votes.toLocaleString()}</span> votes
                  </div>
                  <button
                    onClick={() => vote(c.id)}
                    data-testid={CONTESTANT.voteButton}
                    disabled={voted}
                    className={`h-9 px-3 inline-flex items-center gap-1.5 rounded-sm text-[11px] uppercase tracking-[0.15em] font-bold transition-colors ${
                      voted
                        ? "bg-[#1E8E3E] text-white cursor-not-allowed"
                        : "bg-[#F26A1F] hover:bg-[#D95A15] text-white"
                    }`}
                  >
                    <Heart size={12} fill={voted ? "white" : "none"} />
                    {voted ? "Voted" : "Vote"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 text-white/50">
          No contestants match your filters.
        </div>
      )}
    </div>
  );
}
